import { ColorDynamic, Inline, Stack } from '@superdispatch/ui';
import { Link, Typography } from '@material-ui/core';
import styled from 'styled-components';
import { ArrowForward } from '@material-ui/icons';
import { Box } from '@superdispatch/ui-lab';
import { ExtendedReason } from 'shared/modules/shipper/ShipperReportDTO';
import { ShipperReportContent } from './ShipperReportLayout';

const ReasonButtonContainer = styled.div`
  display: flex;
  align-items: center;
  align-self: stretch;
  gap: 24px;

  cursor: pointer;
  padding: 8px 16px;
  border-radius: 4px;
  border: 1px solid ${ColorDynamic.Silver400};
`;

interface ReasonSelectButtonProps {
  title: string;
  description: string;
  onClick: () => void;
}

function Reason({ title, description, onClick }: ReasonSelectButtonProps) {
  return (
    <ReasonButtonContainer aria-label={title} onClick={onClick}>
      <Stack space="none">
        <Typography variant="body1">{title}</Typography>
        <Typography color="textSecondary">{description}</Typography>
      </Stack>
      <ArrowForward fontSize="small" htmlColor={ColorDynamic.Dark100} />
    </ReasonButtonContainer>
  );
}

interface ShipperReportReasonSelectorProps {
  onReasonSelect: (tag: ExtendedReason) => void;
}

export function ShipperReportReasonSelector({
  onReasonSelect,
}: ShipperReportReasonSelectorProps) {
  return (
    <ShipperReportContent>
      <Stack space="small">
        <Typography variant="h4">What are you reporting?</Typography>
        <Stack space="small">
          <Reason
            title="Double-Brokering"
            description="The broker re-brokered, re-dispatched, or reassigned the load to another carrier without consent or proper authorization."
            onClick={() => {
              onReasonSelect('DOUBLE_BROKERING');
            }}
          />
          <Reason
            title="Non-payment"
            description="The broker or shipper refused to pay the agreed rate for a delivered load according to the dispatched terms."
            onClick={() => {
              onReasonSelect('NON_PAYMENT');
            }}
          />
          <Reason
            title="Unauthorized Dispatching"
            description="The broker or shipper had no authority to dispatch the vehicle(s). The load offer was fraudulently created or listed for transport."
            onClick={() => {
              onReasonSelect('UNAUTHORIZED_DISPATCHING');
            }}
          />
          <Reason
            title="Suspicious Activity"
            description="Unusual or inconsistent actions observed on Super Loadboard, such as fake or irregular load postings, or conflicting shipper or broker information or documents."
            onClick={() => {
              onReasonSelect('SUSPICIOUS_ACTIVITY');
            }}
          />
          <Reason
            title="Other"
            description="Fraudulent or improper activity not covered above."
            onClick={() => {
              onReasonSelect('OTHER');
            }}
          />
        </Stack>
        <Box padding="small" borderRadius="small" backgroundColor="Silver200">
          <Inline>
            <Typography>Are you reporting something else?</Typography>
            <Link
              component="button"
              aria-label="Leave a rating"
              color="primary"
              onClick={(event) => {
                event.preventDefault();
                onReasonSelect('RATING');
              }}
            >
              <Inline verticalAlign="center" space="xxsmall">
                Leave a rating <ArrowForward fontSize="small" />
              </Inline>
            </Link>
          </Inline>
        </Box>
      </Stack>
    </ShipperReportContent>
  );
}
